/**
 * @name LinkGenerator
 * @description Generate a <Link> component or <a>
  element depending on whether url matches Router paths
 * @class
 * @author R.J. LaCount <rlacount@deloitte.com>
 * @author Samantha Bridgehouse <sbridgehouse@lululemon.com>
 *
 * @example
 * <LinkGenerator href={url} className={className} {...otherProps}>{linkText}</LinkGenerator>
 */

// THIRD-PARTY
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { parse } from "url";
import Link from "next/link";
import { useRouter } from "next/router";
import { isKnownRoute } from "next-known-route";

import { onlyText } from "../../utils/react-children";
import { isRegionalizedSubPath } from "../../utils/locale-utils";

export const doesMatchRoute = ({
  url,
  host,
  apiHost,
  proxyApiHost = "",
  cartUrl
}) => {
  // Prefix protocol-relative with https
  let prefixed = url;
  if (url.indexOf("//") === 0) {
    prefixed = `https:${url}`;
  }

  const { pathname = "", search = "", host: hostname } = parse(prefixed);

  const apiHostWithoutProtocol = apiHost
    .substr("https://".length)
    .split(":")[0];
  const proxyApiHostWithoutProtocol = proxyApiHost.substr("https://".length);

  // Include relative (no hostname)
  // Include matching config.host (where site is being served)
  // Include matching config.apiHost (where dar api data is being served)
  // Include matching config.proxyApiHost (applicable only in local dev)
  const checkHostname =
    !hostname ||
    hostname === host ||
    hostname === apiHostWithoutProtocol ||
    hostname === proxyApiHostWithoutProtocol;

  // Check if our router has a match for this path
  const route = isKnownRoute(pathname) || cartUrl === pathname;

  // These routes are hosted on other apps so should be excluded from our router
  const excludedPaths = [
    /\/story\/*/,
    /\/help\/(track-order|returns|return-details|purchase-details|quality-promise)/,
    //This pattern is added to skip the regionalization of country domain in the regional selector
    /^\/*$/
  ];

  const excludedExactPaths = ["/en-ca", "/en-ca/", "/fr-ca", "/fr-ca/"];
  const checkForExclusions = excludedPaths.some((path) =>
    path.test(pathname || "")
  );
  const checkForExactExclusions = excludedExactPaths.some(
    (path) => path === pathname
  );

  // Considered a match if the router and hostname match
  const routeMatch =
    !!route && checkHostname && !checkForExclusions && !checkForExactExclusions;

  // Generate a relative URL for Link component
  const relativeURL = (pathname || "") + (search || "");

  return { routeMatch, relativeURL };
};

/**
 * @param {Object} prop Accepts properties of 'locale' and 'relativeURL'
 * @param {String} prop.locale The i18n locale returned from Next's router
 * @param {String} prop.relativeURL The relative URL which requires prepending
 * @return {String} The relative URL with a conditionally prepended i18n locale (en-us and fr-ca are ignored)
 * @description The function prepends the i18n locale to the URL.
 *              For example:
 *                - An i18n en-ca relative URL of /c/womens-leggings/_/N-8r6 will become /en-ca/c/womens-leggings/_/N-8r6
 *
 *              Next.js' i18n feature will auto prepend the i18n locale, but only client-side. This function
 *              ensures that the SSR href value also contain the i18n locale for SEO purposes.
 */
const prependI18nLocale = ({ locale = "en-us", relativeURL }) => {
  if (!isRegionalizedSubPath(locale)) {
    return relativeURL;
  } else {
    // If the relative URL already contains the locale, return it as-is. This is to prevent double i18n locale
    return relativeURL.includes(locale)
      ? relativeURL
      : `/${locale}${relativeURL}`;
  }
};

const LinkGenerator = ({
  "aria-current": ariaCurrent,
  "aria-disabled": ariaDisabled,
  "aria-hidden": ariaHidden,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "data-component-link": dataComponentLink,
  "data-lulu-attributes": dataLuluAttributes,
  "data-lulu-track": dataLuluTrack,
  "data-product-id": dataProductId,
  "data-product-name": dataProductName,
  "data-product-position": dataProductPosition,
  "data-product-sku": dataProductSku,
  "data-testclass": dataTestclass,
  "data-testid": dataTestId,
  analyticsAttrs,
  apiHost,
  cartUrl,
  children,
  className,
  host,
  href: url,
  linkRef,
  onBlur,
  onClick,
  onFocus,
  onKeyDown,
  onMouseDown,
  onMouseEnter,
  onMouseOver,
  onTouchStart,
  proxyApiHost,
  rel,
  role,
  tabindex: tabIndex,
  target
}) => {
  const { locale } = useRouter() || {};
  if (!url) return null;

  const commonProps = {
    className: classNames({
      link: true,
      [className]: !!className
    }),
    "aria-label": ariaLabel,
    "aria-disabled": ariaDisabled,
    "aria-current": ariaCurrent,
    "aria-labelledby": ariaLabelledBy,
    "aria-hidden": ariaHidden,
    "data-component-link": dataComponentLink,
    "data-testid": dataTestId,
    "data-lulu-track": dataLuluTrack,
    "data-testclass": dataTestclass,
    "data-product-id": dataProductId,
    "data-lulu-attributes": dataLuluAttributes,
    "data-product-name": dataProductName,
    "data-product-position": dataProductPosition,
    "data-product-sku": dataProductSku,
    tabIndex,
    role,
    rel,
    target,
    onClick,
    onKeyDown,
    onMouseEnter,
    onMouseOver,
    onMouseDown,
    onTouchStart,
    onFocus,
    onBlur,
    ref: linkRef
  };

  const { routeMatch, relativeURL } = doesMatchRoute({
    url,
    host,
    apiHost,
    proxyApiHost,
    cartUrl
  });

  const spanForScreenReader = target === "_blank" && (
    <span className="sr-only">(opens a new window)</span>
  );

  let thirdPartyLinkProps = {};

  if (
    !routeMatch &&
    !rel &&
    onlyText(children).toLowerCase().includes("download")
  ) {
    thirdPartyLinkProps = {
      rel: "nofollow"
    };
  }

  return routeMatch ? (
    <Link
      {...commonProps}
      {...analyticsAttrs}
      href={prependI18nLocale({
        locale,
        relativeURL
      })}
      onClick={onClick}
    >
      {children}
      {spanForScreenReader}
    </Link>
  ) : (
    <a {...commonProps} {...analyticsAttrs} {...thirdPartyLinkProps} href={url}>
      {children}
      {spanForScreenReader}
    </a>
  );
};

LinkGenerator.defaultProps = {
  "aria-disabled": undefined,
  "aria-hidden": undefined,
  "aria-label": undefined,
  "aria-labelledby": undefined,
  "data-component-link": undefined,
  "data-testid": undefined,
  analyticsAttrs: undefined,
  children: undefined,
  className: undefined,
  linkRef: undefined,
  onBlur: undefined,
  onClick: undefined,
  onFocus: undefined,
  onKeyDown: undefined,
  onMouseDown: undefined,
  onMouseEnter: undefined,
  onMouseOver: undefined,
  onTouchStart: undefined,
  proxyApiHost: "",
  rel: undefined,
  role: undefined,
  tabindex: undefined,
  target: undefined
};

LinkGenerator.propTypes = {
  "aria-disabled": PropTypes.string,
  "aria-hidden": PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  "aria-label": PropTypes.string,
  "aria-labelledby": PropTypes.string,
  "data-component-link": PropTypes.string,
  "data-testid": PropTypes.string,
  analyticsAttrs: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  apiHost: PropTypes.string.isRequired,
  cartUrl: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  host: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  linkRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseOver: PropTypes.func,
  onTouchStart: PropTypes.func,
  proxyApiHost: PropTypes.string,
  rel: PropTypes.string,
  role: PropTypes.string,
  tabindex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  target: PropTypes.string
};

export default LinkGenerator;
